import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Modal,
  ModalBody,
  Button,
  Text,
  Select,
  Tabs,
  Spacer,
  Input,
  Checkbox,
  Grid,
} from "@flexisaf/flexibull2";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";

import usePapersGet from "../../../hooks/papers/usePapersGet";
import useQuestionCreate from "../../../hooks/questions/useQuestionEditor";
import useSingleQuestionGet from "../../../hooks/questions/useQuestionGet";
import { clientErrorHandler } from "../../../utils/errorHandlers";
import Theme from "../../../theme";
import { OptionItem } from "../../custom/optionItem";
import { returnUpdatedList } from "../../../utils/helpers";
import SectionLoader from "../../custom/sectionLoader";
import useTopicsGet from "../../../hooks/auto-complete/useTopicsGet";
import styled from "styled-components";
import useQuestionsGet from "../../../hooks/questions/useQuestionsGet";
import TinyEditor from "../../custom/tinyEditor";

import { OptionsWrapper, OptionWrapper } from "./styles";
import useGetQuestionImage from "../../../hooks/questions/useGetQuestionImage";
import useSectionsGet from "../../../hooks/sections/useSectionsGet";
import { MyCropper } from "../../ImageUploader/cropper";
import { questionTypes } from "../../../utils/constants";

import useGenerateSolution from "../../../hooks/questions/useGenerateSolution";
import { RiErrorWarningLine } from "react-icons/ri";
import useMarkAnswer from "../../../hooks/questions/useMarkAnswer";

const DEFAULT_OPTION = [
  {
    id: uuidv4(),
    text: "",
    correct: false,
  },
];

const QuestionEditor = ({ open, data, currentPaper, closeModal, refetch }) => {
  const { questions } = useQuestionsGet(currentPaper?.id || "");
  const [selectedPaper, setSelectedPaper] = useState();
  const { loadingSection, sections } = useSectionsGet(selectedPaper?.id);
  const { papers } = usePapersGet({ id: "" });
  const [options, setOptions] = useState(DEFAULT_OPTION);
  const [correctOption, setCorrectOption] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(questions?.count + 1);
  const [image, setImage] = useState("");
  const [hasImage, setHasImage] = useState(false);
  const [hasSection, setHasSection] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [selectedQuestionType, setSelectedQuestionType] = useState(
    questionTypes[0]
  );
  const [shortTextAnsToggle, setShortTextAnsToggle] = useState(false);
  const [answerTexts, setAnswerTexts] = useState([]);
  const [answerTextInput, setAnswerTextInput] = useState("");
  const { imageSrc } = useGetQuestionImage({ id: data?.imageUrl });

  const handleCorrectOption = (option) => {
    if (selectedQuestionType.value === "SINGLE_CHOICE") {
      setCorrectOption(option);
    }

    if (selectedQuestionType.value === "MULTIPLE_CHOICE") {
      if (correctOption === null) {
        setCorrectOption([option]);
      } else {
        setCorrectOption([...correctOption, option]);
      }
    }
  };
  const {
    isEditing,
    createQuestion,
    editQuestion,
    createQuestionWithImage,
    editQuestionWithImage,
  } = useQuestionCreate();
  const {
    isGenerating,
    generateSolution,
    generatedSolution,
    resetGeneratedSolution,
  } = useGenerateSolution();

  const { markQuestion, isMarking, markedOptions } = useMarkAnswer();
  const {
    loadingQuestion,
    getQuestion,
    question,
    reset: resetReducer,
  } = useSingleQuestionGet({
    id: data?.id,
  });

  const editorRef = useRef(null);
  const solutionRef = useRef(null);

  const {
    topicList,
    topic,
    setTopic,
    onTopicChange,
    onTopicSelect,
    resetTopic,
  } = useTopicsGet({
    searchKey: currentPaper?.subject?.name,
  });

  const reset = () => {
    setTopic("");
    setOptions(DEFAULT_OPTION);
    setCorrectOption(null);
    setSelectedSection(null);
    setHasSection(false);
    setImage("");
    setHasImage("");
    setTags([]);
    resetGeneratedSolution();
  };

  useEffect(() => {
    if (data?.id) {
      getQuestion();
    }
  }, [data, getQuestion]);

  useEffect(() => {
    if (!question || !data?.id) return;
    const editQuestionChoice = questionTypes.find(
      (item) => item.value === question.type
    );
    setSelectedQuestionType(editQuestionChoice);
    setTopic(question?.topic || "");
    setTags(question?.tags);
    setQuestionNumber(question?.questionNumber || 0);
    if (question?.section?.id) {
      setHasSection(true);
      setSelectedSection({
        value: question?.section?.id,
        label: question?.section?.title,
      });
    }
    setOptions(
      question?.answerOptions?.map((option) => ({
        id: option?.id,
        text: option?.text,
        correct: option?.isCorrect,
      }))
    );
    const correctOption = question?.answerOptions.find(
      (option) => option?.isCorrect === true
    );

    const multipleChoiceCorrectOption = question?.answerOptions
      .filter((option) => option.isCorrect === true)
      .map((option) => ({
        id: option.id,
        text: option.text,
        isCorrect: option.isCorrect,
      }));

    if (question.type === "SINGLE_CHOICE") {
      setCorrectOption({
        id: correctOption?.id,
        text: correctOption?.text,
        correct: correctOption?.isCorrect,
      });
    }
    if (question.type === "MULTIPLE_CHOICE") {
      setCorrectOption(multipleChoiceCorrectOption);
    }
    if (question.type === "SHORT_TEXT" && question.answerTexts.length) {
      setShortTextAnsToggle(true);
      setAnswerTexts(question.answerTexts);
    } else {
      setShortTextAnsToggle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, question?.answerOptions]);

  useEffect(() => {
    if (currentPaper) {
      setSelectedPaper({
        label: currentPaper?.name || "",
        value: currentPaper?.id || "",
      });
    }
  }, [currentPaper]);

  useEffect(() => {
    if (!correctOption) return;
    const newOptionsList = options?.map((option) => {
      return {
        ...option,
        correct:
          selectedQuestionType.value === "SINGLE_CHOICE"
            ? option?.id === correctOption?.id
            : selectedQuestionType.value === "MULTIPLE_CHOICE"
            ? correctOption?.some((item) => item.id === option.id)
            : false,
      };
    });
    setOptions(newOptionsList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctOption]);

  const onClose = () => {
    reset();
    resetReducer();
    closeModal();
    document.body.style.overflow = "auto";
  };

  const validateFields = (fieldData) => {
    if (
      editorRef.current.getContent() === "" ||
      solutionRef.current.getContent() === "" ||
      topic === ""
    ) {
      clientErrorHandler("Please fill in a question, solution and a topic");
      return;
    } else {
      if (data?.id) {
        image ? editQuestionWithImage(fieldData) : editQuestion(fieldData);
      } else {
        image ? createQuestionWithImage(fieldData) : createQuestion(fieldData);
      }
    }
  };

  const handleSubmit = () => {
    validateFields({
      data: {
        paperId: currentPaper?.id,
        text: editorRef.current ? editorRef.current?.getContent() : "",
        answerOptions:
          selectedQuestionType.value === "SHORT_TEXT" ||
          selectedQuestionType.value === "LONG_TEXT"
            ? []
            : [...options],
        sectionId: selectedSection?.value,
        solution: solutionRef.current ? solutionRef.current?.getContent() : "",
        type: selectedQuestionType?.value,
        questionNumber: questionNumber,
        topic: topic.trim(),
        imageUrl: image ? image : "",
        tags,
        id: data?.id,
        answerTexts:
          selectedQuestionType.value === "SHORT_TEXT" &&
          answerTextInput &&
          answerTexts.length < 10
            ? [...answerTexts, answerTextInput]
            : answerTexts,
      },

      callback: () => {
        onClose();
        refetch();
      },
    });
  };

  const handleAITask = (callback) => {
    const isTextQuestion =
      selectedQuestionType.value === "SHORT_TEXT" ||
      selectedQuestionType.value === "LONG_TEXT";

    const question = editorRef?.current?.getContent();
    const answerOptions = options
      ?.map((opt) => opt.text)
      ?.filter((option) => option);

    if (question && answerOptions?.length > 0 && !isTextQuestion) {
      const payload = {
        question,
        answers: answerOptions,
        sectionId:
          data?.section?.id || (selectedSection ? selectedSection.value : null),
      };

      callback(payload);
    } else if (isTextQuestion && question) {
      const payload = {
        question,
        answers: [],
        sectionId:
          data?.section?.id || (selectedSection ? selectedSection.value : null),
      };

      callback(payload);
    } else {
      clientErrorHandler("Please fill in a question and answer option(s)");
    }
  };

  const autoGenerateSolution = () => {
    handleAITask(generateSolution);
  };

  const handleMarkQuestions = () => {
    handleAITask(markQuestion);
  };

  useEffect(() => {
    if (selectedQuestionType.value === "SINGLE_CHOICE") {
      if (markedOptions) {
        let ans = options[markedOptions[0]];
        if (ans) {
          setCorrectOption(ans);
          return;
        }
      }
    }

    if (selectedQuestionType.value === "MULTIPLE_CHOICE") {
      const multipleChoice = markedOptions?.map((element, index) => {
        return options[element];
      });
      setCorrectOption(multipleChoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markedOptions]);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(options);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setOptions(items);
  };

  const editOption = (option) => {
    const newList = returnUpdatedList(option, options, "id");
    setOptions([...newList]);
  };

  const addNewOption = () => {
    setOptions([...options, { id: uuidv4() }]);
  };

  const deleteOption = (id) => {
    setOptions(options.filter((option) => option.id !== id));
    if (id === correctOption?.id) {
      setCorrectOption(undefined);
    }
  };

  const updateImage = () => {
    setHasImage(true);
  };

  const handleKeyDown = (event, isBlur = false) => {
    const addTag = () => {
      if (tagInput) {
        const tagsCopy = [...tags];
        if (!tagsCopy.includes(tagInput)) {
          tagsCopy.push(tagInput);
          setTags(tagsCopy);
          setTagInput("");
        }
        event.preventDefault();
      } else {
        if (isBlur) {
          setTagInput("");
          return;
        }
      }
    };
    if (!tagInput) return;
    if (isBlur) {
      addTag();
      return;
    }
    switch (event.key) {
      case "Enter":
      case "Tab":
      case " ":
      case ",":
        addTag();
        return;
      default:
        return;
    }
  };

  const handleAnswerTextAdd = () => {
    if (answerTextInput && answerTexts.length < 10) {
      setAnswerTexts([...answerTexts, answerTextInput]);
      setAnswerTextInput("");
    } else return;
  };

  const handleAnswerTextDelete = (id) => {
    const filteredList = answerTexts.filter((_, index) => id !== index);
    setAnswerTexts(filteredList);
    if (!filteredList.length) {
      setShortTextAnsToggle(false);
    }
  };

  return (
    <Modal open={open}>
      <ModalBody width="95%" style={{ height: "95%", maxWidth: "1400px" }}>
        {loadingQuestion && data?.id ? (
          <SectionLoader height="100%" />
        ) : (
          <Box display="flex" height="100%">
            <Box
              width="25%"
              background={Theme.PrimarySurface}
              style={{
                borderRight: `1px solid ${Theme.PrimaryBorderColor}`,
                position: "relative",
              }}
              pad="34px"
            >
              <Text bold block size="16px">
                {data?.id ? "Edit" : "Add a New"} Question
              </Text>

              <Box margin="15px 0" display="block">
                <Select
                  placeholder="Select Paper"
                  label="Paper"
                  spaceTop="40px"
                  spaceBottom="30px"
                  required
                  value={selectedPaper}
                  onChange={(e) => setSelectedPaper(e)}
                  options={
                    currentPaper?.id === undefined
                      ? []
                      : papers?.items?.map((subject) => ({
                          label: subject.name,
                          value: subject.id,
                        }))
                  }
                  style={{ width: "100%" }}
                />
                <Select
                  label="Select Question Type"
                  placeholder="E.g Multiple Choice"
                  block
                  spaceBottom="30px"
                  value={selectedQuestionType}
                  onChange={(value) => {
                    setCorrectOption(null);
                    setSelectedQuestionType(value);
                  }}
                  options={questionTypes}
                  defaultValue={questionTypes[0]}
                />
                <Input
                  placeholder="Ex: 20"
                  label="Question Number"
                  type="number"
                  spaceTop="20px"
                  spaceBottom="40px"
                  block
                  required
                  value={questionNumber}
                  onChange={({ target: { value } }) => setQuestionNumber(value)}
                />
                <Box style={{ position: "relative" }}>
                  <Input
                    placeholder="Ex: Algebra"
                    label="Topic"
                    spaceTop="30px"
                    spaceBottom="30px"
                    block
                    required
                    value={topic}
                    onChange={({ target: { value } }) => onTopicChange(value)}
                  />
                  {topic && topicList && !topicList.includes(topic) && (
                    <SuggestionContainer>
                      {topicList.map((topic) => (
                        <li
                          key={topic}
                          onClick={() => {
                            onTopicSelect(topic);
                            resetTopic();
                          }}
                        >
                          {topic}
                        </li>
                      ))}
                    </SuggestionContainer>
                  )}
                </Box>
              </Box>
              <Box
                align="center"
                width="100%"
                style={{
                  marginTop: "auto",
                  borderTop: `1px solid ${Theme.PrimaryBorderColor}`,
                }}
                pad="20px 0 0 0"
              >
                <Box pad="0px">
                  <Button
                    pale
                    color={Theme.PrimaryGrey}
                    fontColor={Theme.PrimaryGrey}
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    spaceLeft
                    progress={isEditing}
                    onClick={handleSubmit}
                    disabled={
                      (selectedQuestionType.value === "SINGLE_CHOICE" &&
                        !correctOption) ||
                      (selectedQuestionType.value === "MULTIPLE_CHOICE" &&
                        !correctOption) ||
                      // !editorRef.current.getContent() ||
                      // !solutionRef.current.getContent() ||
                      !selectedPaper ||
                      !topic ||
                      (shortTextAnsToggle &&
                        !answerTextInput &&
                        !answerTexts.length)
                    }
                  >
                    {data?.id ? "Edit" : "Add"} question
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box
              width="75%"
              height="100%"
              pad="40px 0 0 0"
              style={{ overflow: "scroll" }}
            >
              <Tabs
                activeTab={0}
                padContent="35px 0 0 0"
                bgColor={Theme.PrimaryBackground}
              >
                <Box label="Question">
                  <Box pad="0px 34px">
                    <Checkbox
                      block
                      style={{ marginRight: "0px" }}
                      label={
                        <Text size="12px">Add a section to this question</Text>
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setHasSection(checked);
                        if (!checked) {
                          setSelectedSection(null);
                        }
                      }}
                      checked={hasSection}
                      size="16px"
                    />

                    {hasSection && (
                      <Select
                        placeholder="Choose a section"
                        // label="Paper"
                        spaceTop="5px"
                        spaceBottom="5px"
                        // required
                        value={selectedSection}
                        onChange={(e) => setSelectedSection(e)}
                        isLoading={loadingSection}
                        options={sections?.items?.map((section) => ({
                          value: section?.id,
                          label: section?.title,
                        }))}
                        style={{ width: "100%" }}
                      />
                    )}
                    <Spacer space="5px" />
                    <Box style={{ width: "100%" }}>
                      <TinyEditor
                        editorRef={editorRef}
                        height={400}
                        value={question?.text}
                        showMenuBar
                      />
                    </Box>

                    <Box pad="24px 0 0 0">
                      {!hasImage && imageSrc === "" ? (
                        <>
                          <Button onClick={() => setHasImage(true)}>
                            Add Image
                          </Button>
                        </>
                      ) : (
                        <>
                          {hasImage && (
                            <>
                              <Box align="center" pad="10px 0">
                                <MyCropper
                                  imageName="question_image"
                                  onUpload={(file) => setImage(file)}
                                  style={{ margin: "auto" }}
                                />
                              </Box>
                              <Box
                                display="flex"
                                style={{
                                  width: "100%",
                                  justifyContent: "center",
                                  marginTop: "5px",
                                }}
                              >
                                <Button
                                  onClick={() => {
                                    setHasImage(false);
                                    setImage(null);
                                  }}
                                  color={Theme.PrimaryRed}
                                  fontColor="#fff"
                                >
                                  {image ? "Delete" : "Cancel"}
                                </Button>
                              </Box>
                            </>
                          )}
                        </>
                      )}
                      {data && imageSrc !== "" && !hasImage && (
                        <Box align="center" pad="10px 0" relative>
                          <Box>
                            <img src={imageSrc} alt="question_image" />
                          </Box>
                          <Box
                            style={{
                              position: "absolute",
                              right: "5rem",
                              top: "10px",
                            }}
                          >
                            <Button
                              onClick={updateImage}
                              color={Theme.PrimaryRed}
                            >
                              <i
                                className="saf-close-circle"
                                style={{ fontSize: "1.2rem" }}
                              />
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      {markedOptions && (
                        <Warning>
                          <span>
                            <RiErrorWarningLine /> Please verify this solution,
                            as it was generated by AI
                          </span>
                        </Warning>
                      )}
                    </Box>
                    {selectedQuestionType.value !== "SHORT_TEXT" &&
                      selectedQuestionType.value !== "LONG_TEXT" && (
                        <Box pad="20px 0px">
                          <Text
                            uppercase
                            bold
                            size="10px"
                            style={{ letterSpacing: "1px" }}
                          >
                            Options
                          </Text>
                          <Spacer space={8} />

                          <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable">
                              {(provided) => {
                                return (
                                  <OptionsWrapper
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                  >
                                    {options.map((option, index) => {
                                      return (
                                        <OptionWrapper
                                          key={option.id}
                                          draggableId={option.id}
                                          index={index}
                                        >
                                          {(provided, snapshot) => {
                                            return (
                                              <OptionItem
                                                provided={provided}
                                                snapshot={snapshot}
                                                option={option}
                                                editOption={editOption}
                                                deleteOption={deleteOption}
                                                disableDelete={
                                                  options.length < 2
                                                }
                                                setCorrectOption={() => {
                                                  handleCorrectOption(option);
                                                }}
                                                isCorrectOption={
                                                  selectedQuestionType.value ===
                                                  "SINGLE_CHOICE"
                                                    ? correctOption?.id ===
                                                      option.id
                                                    : correctOption?.some(
                                                        (item) =>
                                                          item.id === option.id
                                                      )
                                                }
                                                questionType={
                                                  selectedQuestionType?.value
                                                }
                                              />
                                            );
                                          }}
                                        </OptionWrapper>
                                      );
                                    })}
                                  </OptionsWrapper>
                                );
                              }}
                            </Droppable>
                          </DragDropContext>

                          <Box height="5px" />
                          <Box
                            display="flex"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Button
                              color={Theme.PrimaryFontColor}
                              fontColor="#fff"
                              iconLeft={
                                <i
                                  className="saf-add-circle"
                                  style={{ fontSize: "18px" }}
                                />
                              }
                              style={{
                                marginTop: "5px",
                                marginBottom: "15px",
                              }}
                              onClick={addNewOption}
                            >
                              <Text>Add option</Text>
                            </Button>

                            <Button
                              onClick={() => {
                                handleMarkQuestions();
                              }}
                            >
                              {" "}
                              {isMarking
                                ? "Marking..."
                                : "Auto Select Answer"}{" "}
                            </Button>
                          </Box>
                        </Box>
                      )}

                    {selectedQuestionType.value === "SHORT_TEXT" && (
                      <Box pad="10px 0">
                        <Checkbox
                          label="Provide correct answer"
                          checked={shortTextAnsToggle}
                          onChange={(e) => {
                            setShortTextAnsToggle(e.target.checked);
                            if (!e.target.checked) {
                              setAnswerTexts([]);
                              setAnswerTextInput("");
                            }
                          }}
                        />
                        <Spacer space="5px" />
                        {shortTextAnsToggle && (
                          <Grid default="1fr 1fr">
                            <Box>
                              {answerTexts?.map((answerText, index) => (
                                <Box
                                  vAlign
                                  margin="10px 0"
                                  style={{ justifyContent: "space-between" }}
                                >
                                  <p>{answerText}</p>
                                  <Button
                                    color={Theme.PrimaryRed}
                                    height={30}
                                    pad="0 5px"
                                    onClick={() =>
                                      handleAnswerTextDelete(index)
                                    }
                                  >
                                    <i className="saf-trash" />
                                  </Button>
                                </Box>
                              ))}
                              {answerTexts?.length < 10 && (
                                <>
                                  <Input
                                    placeholder="Correct answer"
                                    block
                                    maxLength={32}
                                    value={answerTextInput}
                                    onChange={({ target: { value } }) =>
                                      setAnswerTextInput(value)
                                    }
                                  />
                                  <Spacer heigh={10} />
                                  <Button onClick={() => handleAnswerTextAdd()}>
                                    Add more answers
                                  </Button>
                                </>
                              )}
                            </Box>
                            <Box />
                          </Grid>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box label="Solution">
                  <Box pad="0px 34px">
                    <Box pad="10px 0px">
                      <Button onClick={() => autoGenerateSolution()}>
                        {" "}
                        {isGenerating
                          ? "Loading..."
                          : "Auto Generate Solution"}{" "}
                      </Button>

                      {generatedSolution && (
                        <Warning>
                          <span>
                            <RiErrorWarningLine /> Please verify this solution,
                            as it was generated by AI
                          </span>
                        </Warning>
                      )}
                    </Box>
                    <Box style={{ width: "100%" }}>
                      <TinyEditor
                        editorRef={solutionRef}
                        value={generatedSolution || question?.solution}
                        height={400}
                        showMenuBar
                      />
                    </Box>
                  </Box>
                </Box>
                <Box label="Tag Options">
                  <Grid default="1fr 1fr" gap="30px">
                    <Box pad="0px 34px">
                      <SelectExtend>
                        <Select
                          inputValue={tagInput}
                          isClearable={false}
                          isMulti
                          label="Tags"
                          menuIsOpen={false}
                          onChange={(_, action) => {
                            if (action?.action === "remove-value") {
                              setTags((e) =>
                                e.filter((a) => a !== action.removedValue.value)
                              );
                            }
                          }}
                          onInputChange={setTagInput}
                          onKeyDown={handleKeyDown}
                          onBlur={(ev) => handleKeyDown(ev, true)}
                          width="100%"
                          value={tags.map((a) => ({ label: a, value: a }))}
                          placeholder="Difficulty, Year etc"
                        />
                      </SelectExtend>
                    </Box>
                    <Box />
                  </Grid>
                </Box>
              </Tabs>
            </Box>
          </Box>
        )}
      </ModalBody>
    </Modal>
  );
};

export default QuestionEditor;

const SuggestionContainer = styled.div`
  position: absolute;
  top: 43px;
  z-index: 2;
  width: 100%;
  max-height: 150px;
  overflow-y: scroll;
  background: #fff;
  border-radius: 5px;
  box-shadow: rgba(0, 0, 0, 0.02) 0px 0px 0px 1px;
  & li {
    list-style: none;
    padding: 8px 5px;
    transition: all 0.03s ease-in-out;
    :hover {
      background: ${Theme.PrimaryLight};
      cursor: pointer;
    }
  }
`;

const SelectExtend = styled.div`
  & .flexi-select {
    width: 100% !important;
  }
  & .flexi__placeholder {
    font-weight: 500;
    opacity: 0.5;
  }
`;
const Warning = styled.div`
  border: 1px solid #ffcc00;
  padding: 5px;
  color: #000;
  margin: 5px 0px;
  background: #ffcc00;

  span {
    display: flex;
    align-items: center;
  }
  .error {
    color: red;
  }
  svg {
    /* color: #FFCC00; */
    font-size: large;
    margin-right: 5px;
  }
`;
